import React, { useState, useEffect, useRef } from 'react';
import './ProfilePage.css';
import AddExtraUserModal from './users/AddExtraUserModal';
import UserCard from './users/UserCard';
import ExtraUserCard from './users/ExtraUserCard';
import { useMessage } from '../../contexts/MessageContext';

import { ReactComponent as Spinner } from '../../assets/icons/spinner.svg';
import { ReactComponent as PlusIcon } from '../../assets/icons/plus.svg';

const ProfilePage = () => {
  const showMessage = useMessage();
  const [users, setUsers] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [primary, setPrimary] = React.useState(null);
  const [editingDetails, setEditingDetails] = useState(false);
  const [editingPassword, setEditingPassword] = useState(false);
  const [editingUsers, setEditingUsers] = useState(new Set());
  const originalUsersRef = useRef(null);

  useEffect(() => {
    fetch(process.env.REACT_APP_SERVER_URL + '/api/get-users', {
      method: 'POST',
      headers: {
        'Authorization': localStorage.getItem('token'),
        'Content-Type': 'application/json',
      }
    })
      .then(response => response.json())
      .then(data => {
        if (data.success) {
          setUsers(data.users);
          setPrimary(data.primary);
        }
      })
      .catch(error => console.log(error));
  }, []);

  const addExtraUser = (fullName, email, password) => {
    fetch(process.env.REACT_APP_SERVER_URL + '/api/add-extra-user', {
      method: 'POST',
      headers: {
        'Authorization': localStorage.getItem('token'),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ fullName, email, password })
    })
      .then(response => response.json())
      .then(data => {
        if (data.success) {
          setUsers([...users, {fullName, email}]);
          showMessage('New user successfully added.');
        }
        else {
          showMessage('User could not be added.');
        }
      })
      .catch(error => {
        console.log(error);
        showMessage('User could not be added.');
      });
  };

  const deleteExtraUser = (index) => {
    let user = users[index + 1];
    let id = user.id;

    fetch(process.env.REACT_APP_SERVER_URL + '/api/delete-extra-user', {
      method: 'POST',
      headers: {
        'Authorization': localStorage.getItem('token'),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ id })
    })
      .then(response => response.json())
      .then(data => {
        if (data.success) {
          let newUsers = users.filter((user, i) => i !== index + 1);
          setUsers(newUsers);
          showMessage('User successfully deleted.');
        }
        else {
          showMessage('User could not be deleted.');
        }
      })
      .catch(error => {
        console.log(error);
        showMessage('User could not be deleted.');
      });
  };

  const ensureOriginalUsers = () => {
    if (!originalUsersRef.current) {
      originalUsersRef.current = JSON.parse(JSON.stringify(users));
    }
  };

  const startEditDetails = () => {
    ensureOriginalUsers();
    setEditingDetails(true);
  };

  const startEditPassword = () => {
    ensureOriginalUsers();
    setUsers(prevUsers =>
      prevUsers.map(user => ({
        ...user,
        password: '',
        password2: '',
      }))
    );
    setEditingPassword(true);
  };

  const startEditUser = (index) => {
    ensureOriginalUsers();
    setUsers(prevUsers =>
      prevUsers.map((user, i) => (
        i === index + 1
          ? { ...user, password: '', password2: '' }
          : user
      ))
    );
    setEditingUsers(prev => {
      const next = new Set(prev);
      next.add(index);
      return next;
    });
  };

  const discardDetails = () => {
    if (originalUsersRef.current) {
      setUsers(prevUsers =>
        prevUsers.map((user, i) => (
          i === 0 ? originalUsersRef.current[0] : user
        ))
      );
    }
    setEditingDetails(false);
  };

  const discardPassword = () => {
    if (originalUsersRef.current) {
      setUsers(prevUsers =>
        prevUsers.map((user, i) => (
          i === 0 ? originalUsersRef.current[0] : user
        ))
      );
    }
    setEditingPassword(false);
  };

  const discardUser = (index) => {
    if (originalUsersRef.current) {
      setUsers(prevUsers =>
        prevUsers.map((user, i) => (
          i === index + 1 ? originalUsersRef.current[i] : user
        ))
      );
    }
    setEditingUsers(prev => {
      const next = new Set(prev);
      next.delete(index);
      return next;
    });
  };

  const saveChanges = () => {
    if (primary) {
      fetch(process.env.REACT_APP_SERVER_URL + '/api/update-users', {
        method: 'POST',
        headers: {
          'Authorization': localStorage.getItem('token'),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({users})
      })
        .then(response => response.json())
        .then(data => {
          if (data.success) {
            showMessage('Your personal information was successfully updated.');

            setUsers(prevUsers =>
              prevUsers.map(user => ({
                ...user,
                password: '',
                password2: '',
              }))
            );
            setEditingDetails(false);
            setEditingPassword(false);
            setEditingUsers(new Set());
            originalUsersRef.current = null;
          }
          else {
            showMessage('Something went wrong, changes were not saved. Try again later.');
          }
        })
        .catch(error => {
          console.log(error);
          showMessage('Something went wrong, changes were not saved. Try again later.');
        });
    }
    else {
      fetch(process.env.REACT_APP_SERVER_URL + '/api/update-extra-user', {
        method: 'POST',
        headers: {
          'Authorization': localStorage.getItem('token'),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(users[0]),
      })
        .then(response => response.json())
        .then(data => {
          if (data.success) {
            showMessage('Your personal information was successfully updated.');

            setUsers(prevUsers =>
              prevUsers.map(user => ({
                ...user,
                password: '',
                password2: '',
              }))
            );
            setEditingDetails(false);
            setEditingPassword(false);
            setEditingUsers(new Set());
            originalUsersRef.current = null;
          }
          else {
            showMessage('Something went wrong, changes were not saved. Try again later.');
          }
        })
        .catch(error => {
          console.log(error);
          showMessage('Something went wrong, changes were not saved. Try again later.');
        });
    }
  };

  if (users.length === 0) {
    return (
      <div className='spinner'>
        <Spinner />
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="page-shell page-shell--spacious profile">
      {modalVisible && <AddExtraUserModal setVisible={setModalVisible}
                                          addUser={addExtraUser} />}

      <h2 className="page-header">Profile</h2>

      <div className="page-content-shell">
      {primary && (
        <>
          <UserCard user={users[0]}
                    editable={editingDetails}
                    passwordEditable={editingPassword}
                    onEditDetails={startEditDetails}
                    onDiscardDetails={discardDetails}
                    onSaveDetails={saveChanges}
                    onEditPassword={startEditPassword}
                    onDiscardPassword={discardPassword}
                    onSavePassword={saveChanges}
                    setUser={(user) => setUsers([user, ...users.slice(1)])} />

          <div className="profile-section-divider"></div>
          <div className="section-header__with-buttons">
            <div className="section-title__with-count">
              <h4 className="section-title">Secondary users</h4>
              <span className="section-count">{Math.max(users.length - 1, 0)}</span>
            </div>
            <button className="btn primary compact"
                    type="button"
                    aria-label="Add user"
                    onClick={() => setModalVisible(true)}>
              <PlusIcon className='icon sm on-primary' />
              <span>Add user</span>
            </button>
          </div>

          <div className="additional-users">
            {users.slice(1).map((user, index) => {
              return (
                <ExtraUserCard key={index}
                               user={user}
                               index={index}
                               editable={editingUsers.has(index)}
                               onEdit={() => startEditUser(index)}
                               onDiscard={() => discardUser(index)}
                               onSave={saveChanges}
                               deleteUser={deleteExtraUser}
                               setUser={(user) => {
                                 let newUsers = [...users];
                                 newUsers[index + 1] = user;
                                 setUsers(newUsers);
                               }}
                />
              );
            })}
          </div>
        </>
      )}

      {!primary && (
        <ExtraUserCard user={users[0]}
                       index={0}
                       editable={editingDetails}
                       onEdit={startEditDetails}
                       onDiscard={discardDetails}
                       onSave={saveChanges}
                       deleteUser={null}
                       setUser={(user) => setUsers([user]) } />
      )}
      </div>
    </div>

  );
};

export default ProfilePage;
